import {Box, Card, Grid, Section, Text} from '@primer/react-brand/esm'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {getImageSources, MAX_CONTENT_WIDTH} from '@github-ui/swp-core/lib/utils/images'
import {slugify} from '@github-ui/swp-core/lib/utils/slugify'
import {FlexSuiteIconCards} from '../../../components/FlexSuiteIconCards'
import {FlexSuiteSectionBreakout} from '../../../components/FlexSuiteSectionBreakout'
import {FlexSuiteSectionIntro} from '../../../components/FlexSuiteSectionIntro'
import {FlexSuiteTestimonialBanner, FlexSuiteTestimonialQuote} from '../../../components/FlexSuiteTestimonial'
import {renderWithoutParagraphs} from '../../../utils'
import baseStyles from '../../../styles/base.module.css'
import styles from './IndexSection.module.css'
import type {IndexSectionProps} from './types'

export function IndexSection({
  sectionIntroHeading,
  sectionIntroLabel,
  sectionIntroDescription,
  sectionIntroLink,
  breakoutVariant,
  featureRiverBreakout,
  featureBreakoutTabs,
  cardsVariant,
  cardsIconItems,
  cardsImageItems,
  indexTestimonialVariant,
  indexTestimonialLink,
  indexTestimonialLogo,
  indexTestimonialQuote,
  indexTestimonialImage,
  indexTestimonialLabel,
  indexTestimonialName,
  indexTestimonialTitle,
}: IndexSectionProps) {
  const hasBreakoutContent = Boolean(
    (breakoutVariant === 'Breakout' && featureRiverBreakout) ||
    (breakoutVariant === 'Carousel' && featureBreakoutTabs?.length),
  )
  const imageCardsContent = renderImageCardsContent({cardsVariant, cardsImageItems})
  const iconCardsContent =
    cardsVariant === 'Icon Cards' && cardsIconItems?.length ? <FlexSuiteIconCards cards={cardsIconItems} /> : null
  const socialProofContent = renderSocialProofContent({
    indexTestimonialVariant,
    indexTestimonialLink,
    indexTestimonialLogo,
    indexTestimonialQuote,
    indexTestimonialImage,
    indexTestimonialName,
    indexTestimonialTitle,
  })

  const hasIntroContent = Boolean(
    sectionIntroHeading || sectionIntroLabel || sectionIntroDescription || sectionIntroLink,
  )

  if (!hasIntroContent && !hasBreakoutContent && !imageCardsContent && !iconCardsContent && !socialProofContent) {
    return null
  }

  const sectionId = sectionIntroHeading
    ? slugify(documentToPlainTextString(sectionIntroHeading)) || undefined
    : undefined

  return (
    <Section id={sectionId} fullWidth paddingBlockStart="none" paddingBlockEnd="none">
      <FlexSuiteSectionIntro
        heading={sectionIntroHeading}
        label={sectionIntroLabel}
        description={sectionIntroDescription}
        link={sectionIntroLink}
      />
      <FlexSuiteSectionBreakout
        breakoutVariant={breakoutVariant}
        featureRiverBreakout={featureRiverBreakout}
        featureBreakoutTabs={featureBreakoutTabs}
      />
      {iconCardsContent}
      {imageCardsContent ? (
        <Box marginBlockStart={48} data-testid="category-index-cards">
          <Grid enableGutters={false}>
            <Grid.Column>
              <Box className={`${baseStyles.cardGridFrame} ${baseStyles.gridFrameTop}`}>
                <Grid className={baseStyles.gridContent} columnGap="none">
                  {imageCardsContent}
                </Grid>
              </Box>
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}
      {socialProofContent ? (
        <Box paddingBlockStart="spacious" paddingBlockEnd="spacious" data-testid="category-index-social-proof">
          {indexTestimonialLabel ? (
            <Box marginBlockEnd={16}>
              <Text as="p" size="200" variant="muted" align="center">
                {indexTestimonialLabel}
              </Text>
            </Box>
          ) : null}
          <Grid>
            <Grid.Column>{socialProofContent}</Grid.Column>
          </Grid>
        </Box>
      ) : null}
    </Section>
  )
}

function renderImageCardsContent({
  cardsVariant,
  cardsImageItems,
}: Pick<IndexSectionProps, 'cardsVariant' | 'cardsImageItems'>) {
  if (cardsVariant === 'Image Cards' && cardsImageItems?.length) {
    const mediumColumnSpan = cardsImageItems.length === 2 ? 6 : 4

    return cardsImageItems.map(item => {
      return (
        <Grid.Column key={item.sys.id} span={{xsmall: 12, medium: mediumColumnSpan}} className={styles.imageCardColumn}>
          <Box className={styles.imageCardGridItem} padding={{narrow: 32, regular: 48}}>
            <Card href={item.fields.href} variant="minimal" fullWidth ctaText={item.fields.label}>
              <Card.Image
                as="picture"
                aspectRatio="16:9"
                src={`${item.fields.image.fields.file.url}?fm=webp`}
                alt={item.fields.image.fields.description ?? ''}
                sources={getImageSources(item.fields.image.fields.file.url, {maxWidth: MAX_CONTENT_WIDTH / 3})}
              />
              <Card.Heading>
                {renderWithoutParagraphs(item.fields.heading)}
                {item.fields.alt ? <span className="sr-only"> {item.fields.alt}</span> : null}
              </Card.Heading>
              <Card.Description>{renderWithoutParagraphs(item.fields.description)}</Card.Description>
            </Card>
          </Box>
        </Grid.Column>
      )
    })
  }

  return null
}

function renderSocialProofContent({
  indexTestimonialVariant,
  indexTestimonialLink,
  indexTestimonialLogo,
  indexTestimonialQuote,
  indexTestimonialImage,
  indexTestimonialName,
  indexTestimonialTitle,
}: Pick<
  IndexSectionProps,
  | 'indexTestimonialVariant'
  | 'indexTestimonialLink'
  | 'indexTestimonialLogo'
  | 'indexTestimonialQuote'
  | 'indexTestimonialImage'
  | 'indexTestimonialName'
  | 'indexTestimonialTitle'
>) {
  if (!indexTestimonialVariant || !indexTestimonialQuote) {
    return null
  }

  if (indexTestimonialVariant === 'Quote') {
    return (
      <FlexSuiteTestimonialQuote
        quote={indexTestimonialQuote}
        name={indexTestimonialName}
        title={indexTestimonialTitle}
        link={indexTestimonialLink?.fields}
        logo={indexTestimonialLogo}
      />
    )
  }

  if (indexTestimonialVariant === 'Brand Banner') {
    return (
      <FlexSuiteTestimonialBanner
        quote={indexTestimonialQuote}
        link={indexTestimonialLink?.fields}
        logo={indexTestimonialLogo}
        image={indexTestimonialImage}
      />
    )
  }

  return null
}
